import React, { useEffect, useRef } from 'react';
import { ModalEventCJBody } from 'containers/CahierJournal/EventModal/ModalBody';
import { Field, Form } from 'react-final-form';
import { Sidebar } from 'containers/CahierJournal/EventModal/Sidebar';
import { validateEdtEvent } from '@teetsh/app/src/components/EDT/utils';
import { ModalHeader } from 'containers/CahierJournal/EventModal/ModalHeader';
import { ModalFooter } from 'containers/CahierJournal/EventModal/ModalFooter';
import { CJEvent } from '@teetsh/app/src/types/event';
import Dialog from '@teetsh/app/src/components/Dialog';
import {
  BILAN_EVENT,
  BREAK_EVENT,
  RAPPEL_EVENT,
} from '@teetsh/app/src/containers/constants';
import 'twin.macro';
import { Classroom } from '@teetsh/app/src/types/classroom';
import { getCycle } from './getCycle';
import useCreateTagJoin from '@teetsh/app/src/queries/tags/useCreateTagJoin';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import {
  useCloseCreateFicheDePrepInsideModalDrawer,
  useOpenCreateFicheDePrepInsideModalDrawer,
} from 'containers/CahierJournal/CreateFicheDePrepDrawer/atoms';
import { CreateFicheDePrepDrawerInsideEventModal } from 'containers/CahierJournal/CreateFicheDePrepDrawer';
import { AutoBackupCJSeanceForm } from 'containers/CahierJournal/AutoBackupCJSeance';
import { BodyAndSidebar } from 'components/Layouts/BodyAndSidebar';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { useTrackSuggestions } from 'components/Bo/useBoSuggestionTracking';
import { useIsBelowBreakpoint } from 'components/ResponsiveSwitcher/useIsBelowSize';
import { MobileBodyWithTabs } from './ModalBody/Mobile/MobileBodyWithTabs';
import { StartEndAndType } from './ModalBody/StartEndAndType';
import { hasAClassroomThatStartSunday } from './ModalBody/utils';
import useNotification from 'containers/App/Notifications/useNotification';
import { useTranslation } from 'react-i18next';

interface Props {
  event: Omit<CJEvent, 'id'>;
  onSubmit: (v: CJEvent) => Promise<CJEvent | void>;
  onDelete?: () => Promise<any>;
  closeModal: () => void;
  allClassrooms: Classroom[];
}

export function EventModal(props: Props) {
  const { onSubmit, closeModal, onDelete, event, allClassrooms } = props;
  const { notifySuccess } = useNotification();
  const { t } = useTranslation(['cahierJournal']);
  const { schoolyear, school } = useSchoolSchoolyear();
  const materialInputRef = useRef<HTMLInputElement>(null);
  const ateliersRef = useRef<HTMLDivElement>(null);

  const trackSuggestions = useTrackSuggestions();
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const customFieldsInputRefs = useRef<HTMLInputElement[]>([]);
  const { guestMode } = useUser();
  const createTagJoin = useCreateTagJoin({
    schoolyearId: schoolyear.id,
    schoolId: school.id,
  });
  const openCreateFdpDrawer = useOpenCreateFicheDePrepInsideModalDrawer();
  const closeFdpDrawer = useCloseCreateFicheDePrepInsideModalDrawer();
  useEffect(
    () => () => {
      closeFdpDrawer();
      closeModal();
    },
    [],
  );
  const isMobile = useIsBelowBreakpoint('md');

  return (
    <Form<CJEvent>
      initialValues={event}
      keepDirtyOnReinitialize
      validate={validateEdtEvent}
      mutators={{
        setId: (args, state, utils) => {
          utils.changeValue(state, 'id', () => args[0].id);
        },
      }}
      onSubmit={async values => {
        trackSuggestions({
          page: 'CahierJournal',
          title: values.title,
          matieres: (values?.bo?.matiere || []).map(m => m.value),
          domaines: (values?.bo?.domaine || []).map(d => d.value),
          competences: (values?.bo?.competence || []).map(c => c.value),
          goals: (values?.bo?.goals || []).map(g => g.value),
        });

        await onSubmit(values).then(closeModal);
      }}
    >
      {({
        form,
        handleSubmit,
        valid,
        submitting,
        dirty,
        values,
        hasValidationErrors,
      }) => {
        const cycle = getCycle(values, allClassrooms);

        return (
          <form data-testid="event-modal" onSubmit={handleSubmit}>
            <AutoBackupCJSeanceForm />
            <Dialog.Header>
              <div tw="py-3">
                <ModalHeader
                  onAddTagToClassroom={createTagJoin}
                  classroomIds={values.classroomIds}
                  hideTags={
                    values.type === RAPPEL_EVENT || values.type === BILAN_EVENT
                  }
                />
              </div>
              {/* Ce componsant n'est pas visible, c'est juste pour register id dans le form et pouvoir le mutate */}
              <Field name="id" component={() => <div />} />
            </Dialog.Header>
            <Dialog.Body withSubheader>
              {!guestMode?.enabled && (
                <CreateFicheDePrepDrawerInsideEventModal
                // On met le drawer ici car comme c'est une Dialog ça doit être nesté dans le dialog parent
                // sinon on se retrouve avec problème de focus d'input dans le drawer
                />
              )}
              {isMobile ? (
                <MobileBodyWithTabs
                  disabledTabs={values.type === BREAK_EVENT ? ['details'] : []}
                >
                  <StartEndAndType
                    sundayStart={hasAClassroomThatStartSunday(
                      allClassrooms.filter(cl =>
                        values.classroomIds.includes(cl.id),
                      ),
                    )}
                    valid={valid}
                    disableStartEnd={
                      values.type === RAPPEL_EVENT ||
                      values.type === BILAN_EVENT
                    }
                  />
                  <ModalEventCJBody
                    type={values.type}
                    materialInputRef={materialInputRef}
                    attachmentInputRef={attachmentInputRef}
                    customFieldsInputRefs={customFieldsInputRefs}
                    cycle={cycle}
                    onAddToSeanceCallback={() => {
                      notifySuccess({
                        title: t(
                          'cahierJournal:seance.modal.addToSeanceSuccess',
                        ),
                      });
                    }}
                  />
                  <Sidebar
                    dirty={dirty}
                    cycle={cycle}
                    classrooms={allClassrooms}
                    type={values.type}
                    ateliersRef={ateliersRef}
                    materialInputRef={materialInputRef}
                    customFieldsInputRefs={customFieldsInputRefs}
                    attachmentInputRef={attachmentInputRef}
                    openCreateFdpDrawer={async () => {
                      await onSubmit(values).then(v => {
                        if (v) {
                          form.mutators.setId({ id: v.id });
                        }
                        // OMG le timeout, si on fait pas ça dans le cas de create d'event
                        // values est réinitialisé à l'initial value qu'on retrouve dans CreateCJSeanceModal
                        setTimeout(() => {
                          form.reset({ ...values, id: v?.id });
                        }, 1);
                      });
                      openCreateFdpDrawer(values);
                    }}
                    onAddToSeanceCallback={() => {
                      notifySuccess({
                        title: t(
                          'cahierJournal:seance.modal.addToSeanceSuccess',
                        ),
                      });
                    }}
                    variant="mobile_tab"
                  />
                </MobileBodyWithTabs>
              ) : (
                <BodyAndSidebar>
                  <div tw="min-h-72 flex flex-col gap-6">
                    <StartEndAndType
                      sundayStart={hasAClassroomThatStartSunday(
                        allClassrooms.filter(cl =>
                          values.classroomIds.includes(cl.id),
                        ),
                      )}
                      valid={valid}
                      disableStartEnd={
                        values.type === RAPPEL_EVENT ||
                        values.type === BILAN_EVENT
                      }
                    />
                    <ModalEventCJBody
                      cycle={cycle}
                      ateliersRef={ateliersRef}
                      materialInputRef={materialInputRef}
                      attachmentInputRef={attachmentInputRef}
                      customFieldsInputRefs={customFieldsInputRefs}
                      type={values.type}
                    />
                  </div>
                  <Sidebar
                    dirty={dirty}
                    tw="hidden md:block md:pl-4"
                    cycle={cycle}
                    classrooms={allClassrooms}
                    type={values.type}
                    ateliersRef={ateliersRef}
                    materialInputRef={materialInputRef}
                    customFieldsInputRefs={customFieldsInputRefs}
                    attachmentInputRef={attachmentInputRef}
                    openCreateFdpDrawer={async () => {
                      await onSubmit(values).then(v => {
                        if (v) {
                          form.mutators.setId({ id: v.id });
                        }
                        // OMG le timeout, si on fait pas ça dans le cas de create d'event
                        // values est réinitialisé à l'initial value qu'on retrouve dans CreateCJSeanceModal
                        setTimeout(() => {
                          form.reset({ ...values, id: v?.id });
                        }, 1);
                      });
                      openCreateFdpDrawer(values);
                    }}
                    variant="desktop_sidebar"
                  />
                </BodyAndSidebar>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <ModalFooter
                dirty={dirty}
                hasValidationErrors={hasValidationErrors}
                submitting={submitting}
                closeModal={closeModal}
                onDelete={onDelete}
              />
            </Dialog.Footer>
          </form>
        );
      }}
    </Form>
  );
}
